import { useEffect, useMemo, useState } from 'react';
import { fromUnixTime, getUnixTime } from 'date-fns';
import { useGate } from 'statsig-react';
import { gql } from '@soundxyz/gql-string';
import type { MerkleDropV1Schedule, MintSchedule, RangeEditionV1Schedule } from '@soundxyz/sdk';
import { NotSoundEditionError } from '@soundxyz/sdk/errors';
import { retryAsync } from '@soundxyz/utils';
import { assertAddress } from '@soundxyz/utils/address';
import { NULL_ADDRESS } from '@soundxyz/utils/const';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { useAuth } from '@context/auth/base';
import { useSubscription } from '@graphql/graphql-ws';
import type { FragmentType } from '@graphql/reactQuery';
import {
  fetchQuery,
  getFragment,
  GetLegacySongSaleScheduleDocument,
  MintDataContractsFragmentDoc,
  ReleaseMintDataSubscribeDocument,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useEditionInfo, useEditionMintSchedules } from '@store/sdk/reactQuery';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { FEATURE_GATES } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  subscription ReleaseMintDataSubscribe($releaseId: UUID!) {
    releaseUpdates(id: $releaseId) {
      id
      numSold
      isFirstPhaseCompleted
    }
  }

  fragment MintDataContracts on Release {
    id
    price
    chainId
    numSold
    type
    mintStartTime
    samAddress
    airdropCount
    isFirstPhaseCompleted
    auctionContractType
    quantityLowerBound
    quantityUpperBound
    supplyCutoffTimestamp
    contract {
      id
      contractType
      contractAddress
    }
    scheduleIds {
      minterAddress
      mintIds
    }
  }

  query GetLegacySongSaleSchedule($releaseId: UUID!) {
    release(id: $releaseId) {
      id
      saleSchedules {
        id
        isPresale
        artistContractTotalMinted
        maxMintable
        startTime
        endTime
        price
      }
    }
  }
`);

export const useMintSchedule = ({
  release: releaseFragment,
}: {
  release: FragmentType<MintDataContractsFragmentDoc>;
}) => {
  const release = getFragment(MintDataContractsFragmentDoc, releaseFragment);
  const { auctionContractType } = release;
  const logError = useLogError();

  const { value: enabledSubscriptions } = useGate(FEATURE_GATES.RELEASE_MINT_DATA_SUBSCRIPTIONS);

  const { data: subscriptionData } = useSubscription({
    query: ReleaseMintDataSubscribeDocument,
    variables: {
      releaseId: release.id,
    },
    enabled: enabledSubscriptions,
  });

  const releaseNumSold = Math.max(
    release.numSold,
    subscriptionData?.data.releaseUpdates.numSold || 0,
  );

  const [clientInfo, setClientInfo] = useState<{
    schedules: MintSchedule[];
    totalMinted: bigint;
    totalBurned: bigint;
    loading?: boolean;
  }>({
    schedules: [],
    totalMinted: 0n,
    totalBurned: 0n,
    loading: true,
  });
  const { authUserAddress } = useAuth();

  const isEditionContract =
    release.contract.contractType === 'EDITION' && release.type !== 'ALBUM_TRACK';

  const editionMintSchedules = useEditionMintSchedules(
    {
      editionAddress: release.contract.contractAddress,
      scheduleIds: release.scheduleIds,
      chainId: release.chainId,
      auctionContractType,
    },
    {
      enabled: isEditionContract,
    },
  );

  const editionSaleSchedules = editionMintSchedules.data?.mintSchedules;

  const editionInfo = useEditionInfo(
    {
      editionAddress: release.contract.contractAddress,
      chainId: release.chainId,
      auctionContractType,
    },
    {
      enabled: isEditionContract,
    },
  );

  // Fetch sale schedule
  useEffect(() => {
    const main = async () => {
      /**
       * Sound Contracts!
       *
       * For album drop we are going to default to our API
       * This is because the way we do albums today we generate a new song page for each song in the album
       * It is easier to fetch quantities from our API than to fetch from the contract metadata.
       */
      if (isEditionContract) {
        if (!editionSaleSchedules) return;

        setClientInfo({
          schedules: editionSaleSchedules,
          totalMinted: editionInfo.data?.totalMinted ?? 0n,
          totalBurned: editionInfo.data?.totalBurned ?? 0n,
        });
      } else {
        // Artist Contracts!
        const mintSchedules: MintSchedule[] = [];

        const { data } = await fetchQuery(GetLegacySongSaleScheduleDocument, {
          variables: {
            releaseId: release.id,
          },
        });

        let presaleTotalMinted = 0;

        if (data?.release && data.release.saleSchedules.length > 1) {
          // All presales are just Merkle drops in new contracts
          const merkleTypes: MerkleDropV1Schedule[] = data.release.saleSchedules
            .filter(
              s => new Date(s.startTime).getTime() < fromUnixTime(release.mintStartTime).getTime(),
            )
            .map((s): MerkleDropV1Schedule => {
              presaleTotalMinted = s.artistContractTotalMinted ?? 0;
              assertAddress(release.contract.contractAddress);
              return {
                affiliateFeeBPS: 0,
                editionAddress: release.contract.contractAddress,
                startTime: getUnixTime(new Date(s.startTime)),
                endTime: getUnixTime(new Date(s.endTime)),
                maxMintable: s.maxMintable,
                maxMintablePerAccount: 1,
                merkleRoot: '',
                minterAddress: NULL_ADDRESS,
                mintId: 0n,
                mintPaused: true,
                mintType: 'MerkleDrop',
                price: BigInt(s.price),
                totalMinted: presaleTotalMinted,
                interfaceId: '0x89691c4c',
                platformTransactionFee: 0n,
              };
            });
          mintSchedules.push(...merkleTypes);
        }

        assertAddress(release.contract.contractAddress);

        const ranged: RangeEditionV1Schedule = {
          affiliateFeeBPS: 0,

          // all cutoffTime is in the past now
          cutoffTime: release.mintStartTime,
          editionAddress: release.contract.contractAddress,

          startTime: release.mintStartTime,

          // Technically there is no end date on old contracts
          // so we may not have it on few contracts. So it needs to be a big date.
          endTime: release.supplyCutoffTimestamp || 8640000000,

          maxMintableLower: release.quantityLowerBound,
          maxMintableUpper: release.quantityUpperBound,

          mintType: 'RangeEdition',
          maxMintable() {
            return release.quantityUpperBound;
          },

          maxMintablePerAccount: 1,
          minterAddress: NULL_ADDRESS,
          mintId: 0n,
          mintPaused: true,
          price: BigInt(release.price),

          totalMinted: Math.max(0, release.numSold - presaleTotalMinted - release.airdropCount),
          interfaceId: '0x4d4a2e35',
          platformTransactionFee: 0n,
        };
        mintSchedules.push(ranged);

        setClientInfo({
          schedules: mintSchedules.sort((a, b) => a.startTime - b.startTime),
          totalMinted: BigInt(release.numSold),
          // legacy contracts we do not index burns
          totalBurned: 0n,
        });
      }
    };

    // TODO: We should add a cleanup function
    // Issue is with SDK we do not have a cleaner way to cancel requests
    // TODO: https://linear.app/sound/issue/PROD-2466/cleanup-function-for-mint-schedules-hook
    retryAsync(main, {
      attempts: 3,
      interval: 500,
      logger: {
        // eslint-disable-next-line no-console
        error: args => console.error(args),
        // eslint-disable-next-line no-console
        warn: args => console.warn(args),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        info() {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        debug() {},
        child: function () {
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const parentLogger = this;
          return {
            // eslint-disable-next-line no-console
            error: args => console.error(args),
            // eslint-disable-next-line no-console
            warn: args => console.warn(args),
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            info() {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            debug() {},
            child() {
              return parentLogger;
            },
          };
        },
      },
      operation: 'Retry mint schedules info',
      operationExtra: {},
    }).catch(err => {
      // TODO: Remove `file` when we add source maps https://linear.app/sound/issue/ENG-1280/sentry-sourcemaps
      const level = err instanceof NotSoundEditionError ? 'warning' : 'error';
      logError({
        action: MINTING_ERROR_ACTIONS.RELEASE_MINT_DATA_FETCH_ERROR,
        error: err,
        indexedTags: { file: 'useMintSchedule.ts' },
        errorType: ERROR_TYPE.RELEASE_FETCH_MINT_DATA,
        message: 'Error fetching mint data',
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
        level,
      });
    });
  }, [
    authUserAddress,
    logError,
    release,
    isEditionContract,
    editionSaleSchedules,
    editionInfo.data?.totalMinted,
    editionInfo.data?.totalBurned,
    auctionContractType,
  ]);

  const areSchedulesLoading = editionMintSchedules.isInitialLoading;

  const isFirstPhaseCompleted =
    subscriptionData?.data.releaseUpdates.isFirstPhaseCompleted ?? release.isFirstPhaseCompleted;

  return useMemo(() => {
    return {
      ...clientInfo,
      totalMinted: Math.max(
        bigIntToNumber(clientInfo.totalMinted, { strict: false }),
        releaseNumSold,
      ),
      isSamEnabled: !!release.samAddress,
      areSchedulesLoading,
      isFirstPhaseCompleted,
      auctionContractType,
    };
  }, [
    areSchedulesLoading,
    clientInfo,
    release.samAddress,
    releaseNumSold,
    isFirstPhaseCompleted,
    auctionContractType,
  ]);
};
